import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { SocketService } from '../../../../core/services/socket.service';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { Ip } from 'src/app/shared/models/ip.model';
import { IpreloadWithType } from '../../../systems/services/password.service';

/**
 * ip service
 * @class IpService
 */
@Injectable()
export class IpService {

  /**
   * Injected HttpClient service
   * @type {HttpClient}
   */
  private http = inject(HttpClient);

  /**
   * Injected SocketService service
   * @type {SocketService}
   */
  private socketService = inject(SocketService);

  /**
   * Base API URL
   * @type {string}
   */
  private baseApiUrl = environment.apiUrl + 'company/ip';

  /**
   * Echo instance for WebSocket connection
   * @type {any}
   */
  private echoInstance = null;

  /**
   * Subject for IP console events
   * @type {Subject<IipWithType>}
   */
  private socketIpConsole$: Subject<IipWithType> = new Subject();

  /**
   * Subject for IP application events
   * @type {Subject<IipWithType>}
   */
  private socketIpApplication$: Subject<IipWithType> = new Subject();

  /**
   * starts ip console socket
   * @returns {void}
   */
  startIpConsoleSocket() {
    this.echoInstance = this.socketService.getEchoInstance();
    this.echoInstance.private(`company.${this.socketService.companyId}.ip-console`)
      .listen('.IpRestrictionConsoleAddedEvent', (event) => {
        this.socketIpConsole$.next({ action: 'add', ip: event.ip });
      })
      .listen('.IpRestrictionConsoleDeletedEvent', (event) => {
        this.socketIpConsole$.next({ action: 'delete', ip: event.ip });
      })
      .listen('.IpRestrictionConsoleUpdatedEvent', (event) => {
        this.socketIpConsole$.next({ action: 'update', ip: event.ips });
      });
  }

  /**
   * start ip application socket
   * @returns {void}
   */
  startIpApplicationSocket() {
    this.echoInstance = this.socketService.getEchoInstance();
    this.echoInstance.private(`company.${this.socketService.companyId}.ip-application`)
      .listen('.IpRestrictionApplicationAddedEvent', (event) => {
        this.socketIpApplication$.next({ action: 'add', ip: event.ip });
      })
      .listen('.IpRestrictionApplicationDeletedEvent', (event) => {
        this.socketIpApplication$.next({ action: 'delete', ip: event.ip });
      })
      .listen('.IpRestrictionApplicationUpdatedEvent', (event) => {
        this.socketIpApplication$.next({ action: 'update', ip: event.ips });
      });
  }

  /**
   * subscribes to IP console socket events
   * @returns {Observable<IpreloadWithType>}
   */
  subSocketIpConsole(): Observable<IpreloadWithType> {
    return this.socketIpConsole$;
  }

  /**
   * subscribes to IP application socket events
   * @returns {Observable<IpreloadWithType>}
   */
  subSocketIpApplication(): Observable<IpreloadWithType> {
    return this.socketIpApplication$;
  }

  /**
   * stops the socket connection for IP console
   * @returns {void}
   */
  stopIpConsoleSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.ip-console`);
    }
  }

  /**
   * stops the socket connection for IP application
   */
  stopIpApplicationSocket() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.ip-application`);
    }
  }

  /**
   * retrieves IPs for the console
   * @param {any} filters
   * @returns {Promise<any>}
   */
  getIpsConsole(filters): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '-console', { params: filters }));
  }

  /**
   * retrieves IPs for the application
   * @param {any} filters
   * @returns {Promise<any>}
   */
  getIpsApplication(filters): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '-application', { params: filters }));
  }

  /**
   * updates an IP for the console
   * @param {any} data
   * @returns {Promise<any>}
   */
  updateIpConsole(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '-console', data));
  }

  /**
   * updates an IP for the application
   * @param {any} data
   * @returns {Promise<any>}
   */
  updateIpApplication(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '-application', data));
  }

  /**
   * deletes an IP from the console
   * @param {any} data
   * @returns {Promise<any>}
   */
  deleteIpConsole(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', this.baseApiUrl + '-console', { body: data }));
  }

  /**
   * deletes an IP application
   * @param {any} data
   * @returns {Promise<any>}
   */
  deleteIpApplication(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', this.baseApiUrl + '-application', { body: data }));
  }

  /**
   * adds IP to the console
   * @param {any} data
   * @returns {Promise<any>}
   */
  addIpConsole(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '-console', data));
  }

  /**
   * adds IP to the application.
   * @param {any} data
   * @returns {Promise<any>}
   */
  addIpApplication(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '-application', data));
  }
}

/**
 * Interface IP with an action type
 * @interface IipWithType
 */
export interface IipWithType {
  /**
   * IP data
   * @type {Ip}
   */
  ip: Ip;

  /**
   * action performed on the IP
   * @type {'add' | 'update' | 'delete'}
   */
  action: 'add' | 'update' | 'delete';
}
